import React, {useMemo} from 'react';

import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import {rolesPermissions} from '../../../serverClientShared/sharedUtils';
import {administrationRoleEnum} from '../../../serverClientShared/sharedEnums';
import {PERMISSION_LABELS, ALL_DISPLAY_PERMISSIONS} from './permissionLabels';

const getCombinedPermissions = (trainingRole, contentRole, adminRole) => {
    if (adminRole === administrationRoleEnum.WORKSPACE_ADMIN) {
        return new Set(ALL_DISPLAY_PERMISSIONS);
    }
    const combined = new Set();
    const add = (role) => (rolesPermissions[role] || []).forEach((p) => combined.add(p));
    add(trainingRole);
    add(contentRole);
    add(adminRole);
    return combined;
};

const PermissionItem = ({label, enabled}) => (
    <div
        className="flex items-center gap-2 px-3 py-2 rounded-input text-sm"
        style={enabled ? {
            border: '1px solid color-mix(in srgb, var(--onlinesuccess-normal-color) 50%, transparent)',
            backgroundColor: 'color-mix(in srgb, var(--onlinesuccess-normal-color) 8%, transparent)',
            color: 'var(--main-normal-color)',
            fontWeight: 500,
        } : {
            border: '1px solid var(--panels-normal-color)',
            color: 'var(--main-disabled-color)',
        }}
    >
        {enabled
            ? <CheckBoxOutlinedIcon style={{fontSize: 18, color: 'var(--onlinesuccess-normal-color)'}} />
            : <CheckBoxOutlineBlankIcon style={{fontSize: 18, color: 'var(--main-disabled-color)'}} />
        }
        {label}
    </div>
);

const PermissionsModal = ({open, title, trainingRole, contentRole, adminRole, onClose}) => {
    const combined = useMemo(
        () => getCombinedPermissions(trainingRole, contentRole, adminRole),
        [trainingRole, contentRole, adminRole],
    );

    const enabledCount = ALL_DISPLAY_PERMISSIONS.filter((p) => combined.has(p)).length;
    const disabledCount = ALL_DISPLAY_PERMISSIONS.length - enabledCount;

    return (
        <Dialog open={open} onClose={onClose} maxWidth={false} style={{zIndex: 3000}} PaperProps={{className: 'rounded-lg', style: {width: 580}}}>
            <div className="flex flex-col gap-3 px-6 pt-4 pb-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <InfoOutlinedIcon style={{fontSize: 22}} className="text-link-normal" />
                        <div>
                            <div className="text-base font-semibold text-main-normal">{title || 'Permissions'}</div>
                            <div className="text-xs text-main-light">{enabledCount} enabled · {disabledCount} disabled</div>
                        </div>
                    </div>
                    <CloseIcon style={{fontSize: 18}} className="text-main-light cursor-pointer hover:text-main-normal" onClick={onClose} />
                </div>

                <div className="grid grid-cols-2 gap-1.5 max-h-[60vh] overflow-y-auto">
                    {ALL_DISPLAY_PERMISSIONS.map((perm) => (
                        <PermissionItem key={perm} label={PERMISSION_LABELS[perm]} enabled={combined.has(perm)} />
                    ))}
                </div>

                <div className="flex justify-end">
                    <button
                        type="button"
                        className="px-4 py-2 rounded-button text-sm font-bold text-classic-button-normal-text bg-classic-button-normal cursor-pointer hover:bg-classic-button-hover border-none"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </Dialog>
    );
};

export default PermissionsModal;
